// CourseCovers — inline SVG illustrations for the Dashboard course cards.
//
// One illustration per known course slug. Falls back to a generic letter
// tile (the original behaviour) when the slug isn't recognised. Inline SVG
// over raster art for the same reasons the F2C lockup is inline: crisp at
// any density, themable via brand tokens, zero asset pipeline.
//
// Aspect ratio matches the CourseCover container (16:9) so the illustration
// fills the tile without object-fit distortion.

import React from 'react';

// --------------------------------------------------------------------------
// 1. Problem Solving: Flow First, Code Next — flowchart on the left,
//    code on the right, "→" in between. Visually narrates the Flow→Code
//    pedagogy the course is named after.
// --------------------------------------------------------------------------
const ProblemSolvingCover = () => (
  <svg viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    {/* Soft gradient background */}
    <defs>
      <linearGradient id="ps-bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"  stopColor="#eff6ff" />
        <stop offset="100%" stopColor="#ecfdf5" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="url(#ps-bg)" />

    {/* Flow side (left) — three nodes connected vertically */}
    <g transform="translate(20, 26)">
      {/* Input node — blue rounded rect */}
      <rect x="0" y="0"  width="92" height="28" rx="14" fill="#3b82f6" />
      <text x="46" y="18" fontSize="11" fill="white" textAnchor="middle"
            fontFamily="ui-sans-serif, system-ui" fontWeight="600">Input</text>
      <line x1="46" y1="28" x2="46" y2="46" stroke="#94a3b8" strokeWidth="2" strokeDasharray="3 3" />

      {/* Decision node — amber diamond */}
      <polygon points="46,46 86,68 46,90 6,68" fill="#f97316" opacity="0.85" />
      <text x="46" y="72" fontSize="10" fill="white" textAnchor="middle"
            fontFamily="ui-sans-serif, system-ui" fontWeight="600">If x &gt; 0?</text>
      <line x1="46" y1="90" x2="46" y2="108" stroke="#94a3b8" strokeWidth="2" strokeDasharray="3 3" />

      {/* Output node — emerald rounded rect */}
      <rect x="0" y="108" width="92" height="28" rx="14" fill="#10b981" />
      <text x="46" y="126" fontSize="11" fill="white" textAnchor="middle"
            fontFamily="ui-sans-serif, system-ui" fontWeight="600">Print</text>
    </g>

    {/* Connector arrow (Flow → Code) */}
    <g transform="translate(130, 84)">
      <line x1="0" y1="6" x2="38" y2="6" stroke="#6366f1" strokeWidth="3" strokeLinecap="round" />
      <polygon points="38,0 50,6 38,12" fill="#6366f1" />
      <text x="25" y="-4" fontSize="9" fill="#6366f1" textAnchor="middle"
            fontFamily="ui-sans-serif, system-ui" fontWeight="700" letterSpacing="0.5">CODE</text>
    </g>

    {/* Code side (right) — terminal-like window with C-ish lines */}
    <g transform="translate(196, 26)">
      <rect x="0" y="0" width="108" height="128" rx="8" fill="#0f172a" />
      {/* Window chrome dots */}
      <circle cx="10" cy="11" r="3" fill="#ef4444" />
      <circle cx="22" cy="11" r="3" fill="#f59e0b" />
      <circle cx="34" cy="11" r="3" fill="#10b981" />

      {/* Code lines — each a colored capsule (syntax-highlight feel) */}
      <rect x="10" y="32" width="48" height="6" rx="2" fill="#94a3b8" opacity="0.45" />
      <rect x="10" y="46" width="80" height="6" rx="2" fill="#60a5fa" opacity="0.85" />
      <rect x="18" y="60" width="64" height="6" rx="2" fill="#f1f5f9" opacity="0.75" />
      <rect x="18" y="74" width="52" height="6" rx="2" fill="#a78bfa" opacity="0.85" />
      <rect x="26" y="88" width="44" height="6" rx="2" fill="#34d399" opacity="0.85" />
      <rect x="26" y="102" width="56" height="6" rx="2" fill="#94a3b8" opacity="0.55" />
      <rect x="10" y="116" width="20" height="6" rx="2" fill="#f1f5f9" opacity="0.75" />
    </g>
  </svg>
);

// --------------------------------------------------------------------------
// 2. Generic CodeWindowCover — light "editor" frame with the language's
//    signature line big and bold. Used for the per-language courses
//    (C / C++ / Java / Python) so the four sibling tracks share a visual
//    language. Light theme so the dashboard's bright background doesn't
//    read as a row of dark holes.
// --------------------------------------------------------------------------
const CodeWindowCover = ({ accent, language, signature, sigil }) => {
  const ext = language === 'C' ? 'c' : language === 'C++' ? 'cpp' : language === 'Java' ? 'java' : 'py';
  // The big faint background letter(s) default to the language, but a course
  // can override (e.g. "DS" for Data Structures in C, which is still a .c file).
  const bigSigil = sigil || language;
  return (
    <svg viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <defs>
        <linearGradient id={`cw-bg-${language}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#f8fafc" />
          <stop offset="100%" stopColor="#eef2f7" />
        </linearGradient>
      </defs>
      <rect width="320" height="180" fill={`url(#cw-bg-${language})`} />

      {/* Subtle background sigil — large language letter, low opacity */}
      <text x="270" y="160" fontSize="120" fill={accent} opacity="0.10"
            fontFamily="ui-monospace, monospace" fontWeight="700" textAnchor="middle">
        {bigSigil}
      </text>

      {/* Filename label — no chrome, just the filename in a small slate
          monospace at the top-left so the student can identify the source. */}
      <text x="20" y="24" fontSize="11" fill="#64748b"
            fontFamily="ui-monospace, monospace">main.{ext}</text>

      {/* Code body — sits below the filename label. */}
      <g transform="translate(28, 70)">
        <text x="0" y="0" fontSize="13" fill="#94a3b8"
              fontFamily="ui-monospace, monospace">// {language}</text>
        <text x="0" y="28" fontSize="16" fill={accent}
              fontFamily="ui-monospace, monospace" fontWeight="700">
          {signature}
        </text>
        <text x="0" y="58" fontSize="13" fill="#cbd5e1"
              fontFamily="ui-monospace, monospace">_</text>
        <rect x="14" y="49" width="8" height="14" fill={accent} opacity="0.85">
          <animate attributeName="opacity" values="0.85;0.20;0.85" dur="1.4s" repeatCount="indefinite" />
        </rect>
      </g>
    </svg>
  );
};

// --------------------------------------------------------------------------
// Map from course slug → cover. Add new courses here when they ship.
// --------------------------------------------------------------------------
const COVERS_BY_SLUG = {
  'problem-solving-flow-first-code-next-a-guided-experience': () => <ProblemSolvingCover />,
  'c-programming':      () => <CodeWindowCover accent="#3b82f6" language="C"      signature='printf("Hello, C");' />,
  'data-structures-in-c': () => <CodeWindowCover accent="#6366f1" language="C" sigil="DS" signature='struct Node *next;' />,
  'cpp-programming':    () => <CodeWindowCover accent="#a78bfa" language="C++"    signature='cout << "Hello, C++";' />,
  'java-programming':   () => <CodeWindowCover accent="#f97316" language="Java"   signature='System.out.println("Hi");' />,
  'python-programming': () => <CodeWindowCover accent="#10b981" language="Python" signature='print("Hello, Python")' />,
};

export const hasCustomCover = (slug) => Boolean(slug && COVERS_BY_SLUG[slug]);

export const CourseCoverArt = ({ slug }) => {
  const render = slug && COVERS_BY_SLUG[slug];
  if (!render) return null;
  return render();
};

export default CourseCoverArt;
